import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Container,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/Print";
import { authenticatedFetch } from "../../utils/api";
import { usePermissions } from "../../utils/permissions";
import DiaCircle from "../DiaCircle";
import GaugeChip from "../GaugeChip";
import ProgressBar from "../ProgressBar";
import { formatInteger, truncateText, PROGRAM_NAME_MAX_WIDTH, truncatedTextStyle } from "../../utils/formatters";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface OrgSettings {
  gstin: string;
  phone_number: string;
  org_name: string;
  address: string;
  email: string;
  state_code: string;
}

interface ProgramDetail {
  id: number;
  dia: number;
  gauge: string;
  program_type: string;
  weight_in_kilo: number;
  number_of_rolls?: number;
  status?: string;
  completed_weight?: number;
}

interface Program {
  id: number;
  dia: number | string;
  gauge: string;
  program_type?: string;
  total_weight: number | string;
  done_weight: number | string;
  progress: number | string;
  status: string;
  design?: string;
  lot?: string;
  specs_display?: string;
  types_display?: string;
  program_details: ProgramDetail[];
}

interface ReceivedNoteDetail {
  id?: number;
  counts: number;
  number_of_bags: number;
  weight: number;
  mill?: string;
  color?: string;
  _destroy?: boolean;
}

interface DeliveryDetail {
  id: number;
  dia: number;
  gauge: string;
  rolls: number;
  weight: number;
}

interface Delivery {
  id: number;
  delivery_date: string;
  delivery_person_name: string;
  delivery_person_phone: string;
  delivery_vehicle_number: string;
  calculated_total_weight: number | string;
  calculated_total_rolls: number | string;
  specs_display: string;
  program: {
    id: number;
    specs_display: string;
  };
  delivery_details: DeliveryDetail[];
}

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
}

interface ReceivedNote {
  id: number;
  company_id: number;
  company: {
    id: number;
    name: string;
  };
  received_note_details: ReceivedNoteDetail[];
  representative_name?: string;
  representative_phone?: string;
  programs?: Program[];
  deliveries?: Delivery[];
  colors_display?: string;
  created_at: string;
  total_weight: number;
  total_bags: number;
  counts_display: string;
  mills_display: string;
}

interface Company {
  id: number;
  name: string;
}

const received_noteDetailSchema = z.object({
  id: z.number().optional(),
  counts: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Counts is required" }))
    .refine((val) => Number.isInteger(val), "Counts must be an integer")
    .refine((val) => val > 0, "Counts must be greater than 0"),
  number_of_bags: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Number of bags is required" }))
    .refine((val) => Number.isInteger(val), "Number of bags must be an integer")
    .refine((val) => val > 0, "Number of bags must be greater than 0"),
  weight: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Weight is required" }))
    .refine((val) => val > 0, "Weight must be greater than 0"),
  mill: z.string().optional(),
  color: z.string().optional(),
  _destroy: z.boolean().optional(),
});

const received_noteSchema = z.object({
  company_id: z.number().int(),
  representative_name: z.string().optional(),
  representative_phone: z
    .number()
    .int("Phone number must be an integer")
    .nullable()
    .optional(),
  received_note_details_attributes: z
    .array(received_noteDetailSchema)
    .min(1, "At least one received_note detail is required"),
});

type ReceivedNoteFormData = z.infer<typeof received_noteSchema>;

export default function ReceivedNoteDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { canUpdate, canDelete } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [received_note, setReceivedNote] = useState<ReceivedNote | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [orgSettings, setOrgSettings] = useState<OrgSettings | null>(null);

  const { control, handleSubmit, reset, watch } = useForm<ReceivedNoteFormData>({
    resolver: zodResolver(received_noteSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "received_note_details_attributes",
  });

  const received_noteDetails = watch("received_note_details_attributes");

  useEffect(() => {
    fetchReceivedNote();
    fetchCompanies();
    fetchMachines();
    fetchOrgSettings();
  }, [id]);

  const fetchOrgSettings = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/organization_settings`);
      if (response.ok) {
        const data = await response.json();
        setOrgSettings(data);
      }
    } catch (err) {
      console.error("Error fetching organization settings:", err);
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/machines`);
      if (response.ok) {
        const data = await response.json();
        setMachines(data);
      }
    } catch (err) {
      console.error("Error fetching machines:", err);
    }
  };

  const fetchReceivedNote = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/received_notes/${id}`);
      if (!response.ok) {
        throw new Error("Failed to fetch received_note");
      }

      const data = await response.json();
      setReceivedNote(data);
      reset({
        company_id: data.company_id,
        representative_name: data.representative_name || "",
        representative_phone: data.representative_phone
          ? Number(data.representative_phone)
          : null,
        received_note_details_attributes: data.received_note_details.map((d: ReceivedNoteDetail) => ({
          id: d.id,
          counts: Number(d.counts),
          number_of_bags: Number(d.number_of_bags),
          weight: Number(d.weight),
          mill: d.mill || "",
          color: d.color || "",
        })),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/companies?per_page=1000&minimal=true`);
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
      // Silently handle failure - user may not have access to companies list
    } catch (err) {
      console.error("Error fetching companies:", err);
    }
  };

  const onSubmit = async (data: ReceivedNoteFormData) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/received_notes/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ received_note: data }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      const updatedReceivedNote = await response.json();
      setReceivedNote(updatedReceivedNote);
      setIsEditing(false);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this received note?")) {
      setIsDeleting(true);
      setError("");
      try {
        const response = await authenticatedFetch(`${API_URL}/received_notes/${id}`, {
          method: "DELETE",
        });
        
        if (!response.ok) {
          throw new Error("Failed to delete received_note");
        }

        // Successfully deleted (204 No Content)
        navigate("/received-notes");
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
        setIsDeleting(false);
      }
    }
  };

  const calculateTotalWeight = () => {
    return received_noteDetails
      ?.reduce((sum, detail) => {
        const bags = detail?.number_of_bags || 0;
        const weight = detail?.weight || 0;
        return sum + bags * weight;
      }, 0)
      .toFixed(2);
  };

  const calculateTotalBags = () => {
    return received_noteDetails?.reduce(
      (sum, detail) => sum + (detail?.number_of_bags || 0),
      0
    );
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: 4 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          spacing={2} 
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <IconButton 
              onClick={() => navigate("/received-notes")} 
              color="primary" 
              size={isMobile ? "small" : "medium"}
              className="no-print"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant={isMobile ? "h6" : "h5"} component="h2" sx={{ flexGrow: 1 }}>
              {isEditing ? t('receivedNotes.editTitle') : t('receivedNotes.details')}
            </Typography>
          </Stack>
          {!isEditing && (
            <Stack 
              direction="row" 
              spacing={1} 
              sx={{ width: { xs: '100%', sm: 'auto' }, ml: { xs: 0, sm: 'auto' } }}
              className="no-print"
            >
              <IconButton
                onClick={handlePrint}
                color="primary"
                title="Print"
                size={isMobile ? "small" : "medium"}
              >
                <PrintIcon />
              </IconButton>
            </Stack>
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

          {isEditing ? (
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{t('basicInformation')}</Typography>
                  <Stack spacing={2}>
                    <Controller
                      name="company_id"
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormControl fullWidth error={!!fieldState.error} size={isMobile ? "small" : "medium"}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>{t('receivedNotes.company')} *</Typography>
                          <Select {...field} value={field.value || ""} displayEmpty>
                            <MenuItem value="" disabled>{t('selectCompany')}</MenuItem>
                            {companies.map((company) => (
                              <MenuItem key={company.id} value={company.id}>
                                {company.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {fieldState.error && (
                            <FormHelperText>{fieldState.error.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="representative_name"
                          control={control}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              label={t('representativeName')}
                              placeholder={t('optional')}
                              fullWidth
                              size={isMobile ? "small" : "medium"}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="representative_phone"
                          control={control}
                          render={({
                            field: { onChange, value, ...field },
                            fieldState,
                          }) => (
                            <TextField
                              {...field}
                              value={value || ""}
                              onChange={(e) =>
                                onChange(e.target.value ? Number(e.target.value) : null)
                              }
                              label={t('representativePhone')}
                              placeholder={t('optional')}
                              type="tel"
                              fullWidth
                              size={isMobile ? "small" : "medium"}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </CardContent>
              </Card>

              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={2}
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="h6">{t('receivedNotes.details')}</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() =>
                        append({
                          counts: "" as any,
                          number_of_bags: "" as any,
                          weight: "" as any,
                          mill: "",
                          color: "",
                        })
                      }
                      fullWidth={isMobile}
                    >
                      {t('addDetail')}
                    </Button>
                  </Stack>

                  {isMobile ? (
                    /* Mobile Card-Based Layout */
                    <Stack spacing={2}>
                      {fields.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 3 }}>
                          <Typography variant="body2" color="text.secondary" align="center">
                            {t('noDetailsAdded')}
                          </Typography>
                        </Paper>
                      ) : (
                        fields.map((field, index) => {
                          const detail = received_noteDetails[index];
                          const rowTotal = detail?.number_of_bags && detail?.weight 
                            ? (detail.number_of_bags * detail.weight).toFixed(2)
                            : "0.00";
                          
                          return (
                            <Paper key={field.id} variant="outlined" sx={{ p: 2 }}>
                              <Stack spacing={2}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {t('receivedNotes.detailNumber')}{index + 1}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => remove(index)}
                                    disabled={fields.length === 1}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Stack>

                                <Grid container spacing={2}>
                                  <Grid item xs={6}>
                                    <Controller
                                      name={`received_note_details_attributes.${index}.counts`}
                                      control={control}
                                      render={({
                                        field: { onChange, value, ...fieldProps },
                                        fieldState,
                                      }) => (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                            {t('receivedNotes.counts')} *
                                          </Typography>
                                          <TextField
                                            {...fieldProps}
                                            value={value || ""}
                                            onChange={(e) =>
                                              onChange(e.target.value ? Number(e.target.value) : undefined)
                                            }
                                            type="number"
                                            size="small"
                                            inputProps={{ step: "1" }}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            fullWidth
                                            placeholder="e.g. 40"
                                          />
                                        </Box>
                                      )}
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Controller
                                      name={`received_note_details_attributes.${index}.number_of_bags`}
                                      control={control}
                                      render={({
                                        field: { onChange, value, ...fieldProps },
                                        fieldState,
                                      }) => (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                            {t('receivedNotes.numberOfBags')} *
                                          </Typography>
                                          <TextField
                                            {...fieldProps}
                                            value={value || ""}
                                            onChange={(e) =>
                                              onChange(e.target.value ? Number(e.target.value) : undefined)
                                            }
                                            type="number"
                                            size="small"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            fullWidth
                                            placeholder="e.g. 10"
                                          />
                                        </Box>
                                      )}
                                    />
                                  </Grid>
                                </Grid>

                                <Grid container spacing={2}>
                                  <Grid item xs={6}>
                                    <Controller
                                      name={`received_note_details_attributes.${index}.weight`}
                                      control={control}
                                      render={({
                                        field: { onChange, value, ...fieldProps },
                                        fieldState,
                                      }) => (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                            {t('receivedNotes.weightPerBag')} *
                                          </Typography>
                                          <TextField
                                            {...fieldProps}
                                            value={value || ""}
                                            onChange={(e) =>
                                              onChange(e.target.value ? Number(e.target.value) : undefined)
                                            }
                                            type="number"
                                            size="small"
                                            inputProps={{ step: "0.01" }}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            fullWidth
                                            placeholder="e.g. 50"
                                          />
                                        </Box>
                                      )}
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Controller
                                      name={`received_note_details_attributes.${index}.mill`}
                                      control={control}
                                      render={({ field: fieldProps, fieldState }) => (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                            {t('receivedNotes.mill')}
                                          </Typography>
                                          <TextField
                                            {...fieldProps}
                                            size="small"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            fullWidth
                                            placeholder={t('optional')}
                                          />
                                        </Box>
                                      )}
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Controller
                                      name={`received_note_details_attributes.${index}.color`}
                                      control={control}
                                      render={({ field: fieldProps, fieldState }) => (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                            {t('receivedNotes.color')}
                                          </Typography>
                                          <TextField
                                            {...fieldProps}
                                            size="small"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            fullWidth
                                            placeholder={t('optional')}
                                          />
                                        </Box>
                                      )}
                                    />
                                  </Grid>
                                </Grid>

                                <Paper 
                                  variant="outlined" 
                                  sx={{ 
                                    p: 1.5, 
                                    bgcolor: 'primary.50', 
                                    borderColor: 'primary.200',
                                    borderRadius: 1
                                  }}
                                >
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption" color="text.secondary">
                                      {t('receivedNotes.totalWeight')}
                                    </Typography>
                                    <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                                      {rowTotal} kg
                                    </Typography>
                                  </Stack>
                                </Paper>
                              </Stack>
                            </Paper>
                          );
                        })
                      )}

                      {fields.length > 0 && (
                        <>
                          <Divider />
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle2" fontWeight="bold">{t('grandTotal')}:</Typography>
                              <Stack direction="row" spacing={3}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">{t('receivedNotes.bags')}</Typography>
                                  <Typography variant="body2" fontWeight="bold">{calculateTotalBags()}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">{t('receivedNotes.weight')}</Typography>
                                  <Typography variant="body2" fontWeight="bold">{calculateTotalWeight()} kg</Typography>
                                </Box>
                              </Stack>
                            </Stack>
                          </Paper>
                        </>
                      )}
                    </Stack>
                  ) : (
                    /* Desktop Table Layout */
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('receivedNotes.counts')}</TableCell>
                            <TableCell>{t('receivedNotes.numberOfBags')}</TableCell>
                            <TableCell>{t('receivedNotes.weightPerBag')}</TableCell>
                            <TableCell>{t('receivedNotes.totalWeightKg')}</TableCell>
                            <TableCell>{t('receivedNotes.mill')}</TableCell>
                            <TableCell>{t('receivedNotes.color')}</TableCell>
                            <TableCell width={60}>{t('action')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fields.map((field, index) => {
                            const detail = received_noteDetails[index];
                            const rowTotal = detail?.number_of_bags && detail?.weight 
                              ? (detail.number_of_bags * detail.weight).toFixed(2)
                              : "0.00";
                            
                            return (
                              <TableRow key={field.id}>
                                <TableCell>
                                  <Controller
                                    name={`received_note_details_attributes.${index}.counts`}
                                    control={control}
                                    render={({
                                      field: { onChange, value, ...fieldProps },
                                      fieldState,
                                    }) => (
                                      <TextField
                                        {...fieldProps}
                                        value={value || ""}
                                        onChange={(e) =>
                                          onChange(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined
                                          )
                                        }
                                        type="number"
                                        size="small"
                                        inputProps={{ step: "1" }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                      />
                                    )}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Controller
                                    name={`received_note_details_attributes.${index}.number_of_bags`}
                                    control={control}
                                    render={({
                                      field: { onChange, value, ...fieldProps },
                                      fieldState,
                                    }) => (
                                      <TextField
                                        {...fieldProps}
                                        value={value || ""}
                                        onChange={(e) =>
                                          onChange(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined
                                          )
                                        }
                                        type="number"
                                        size="small"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                      />
                                    )}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Controller
                                    name={`received_note_details_attributes.${index}.weight`}
                                    control={control}
                                    render={({
                                      field: { onChange, value, ...fieldProps },
                                      fieldState,
                                    }) => (
                                      <TextField
                                        {...fieldProps}
                                        value={value || ""}
                                        onChange={(e) =>
                                          onChange(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined
                                          )
                                        }
                                        type="number"
                                        size="small"
                                        inputProps={{ step: "0.01" }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                      />
                                    )}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {rowTotal}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Controller
                                    name={`received_note_details_attributes.${index}.mill`}
                                    control={control}
                                    render={({ field: fieldProps, fieldState }) => (
                                      <TextField
                                        {...fieldProps}
                                        size="small"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                      />
                                    )}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Controller
                                    name={`received_note_details_attributes.${index}.color`}
                                    control={control}
                                    render={({ field: fieldProps, fieldState }) => (
                                      <TextField
                                        {...fieldProps}
                                        size="small"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                      />
                                    )}
                                  />
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => remove(index)}
                                    disabled={fields.length === 1}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow sx={{ bgcolor: "action.hover" }}>
                            <TableCell>
                              <strong>{t('total')}</strong>
                            </TableCell>
                            <TableCell>
                              <strong>{calculateTotalBags()}</strong>
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell>
                              <strong>{calculateTotalWeight()} kg</strong>
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setIsEditing(false)}
                  fullWidth={isMobile}
                  size={isMobile ? "medium" : "large"}
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  fullWidth={isMobile}
                  size={isMobile ? "medium" : "large"}
                >
                  {t('saveChanges')}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              {/* Basic Information Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{t('basicInformation')}</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block">{t('noteId')}</Typography>
                      <Typography variant="body1" fontWeight={500}>#{received_note?.id}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block">{t('created')}</Typography>
                      <Typography variant="body2">
                        {new Date(received_note?.created_at || "").toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">{t('receivedNotes.company')}</Typography>
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        onClick={() => navigate(`/companies/${received_note?.company?.id}`)}
                        sx={{
                          cursor: "pointer",
                          color: "primary.main",
                          textDecoration: "underline",
                          "&:hover": { color: "primary.dark" },
                        }}
                      >
                        {received_note?.company?.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">{t('receivedNotes.colors')}</Typography>
                      <Typography variant="body1" fontWeight={500}>{received_note?.colors_display || "-"}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">{t('representative')}</Typography>
                      <Typography variant="body2">{received_note?.representative_name || "-"}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">{t('phone')}</Typography>
                      <Typography variant="body2">{received_note?.representative_phone || "-"}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Received Note Details Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{t('receivedNotes.details')}</Typography>
                  
                  {isMobile ? (
                    /* Mobile Card-Based View */
                    <Stack spacing={2}>
                      {received_note?.received_note_details.map((detail, index) => (
                        <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                          <Stack spacing={1.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Chip 
                                label={`${formatInteger(detail.counts)}s`} 
                                size="small" 
                                color="primary"
                                sx={{ fontWeight: 'bold' }}
                              />
                              <Stack direction="row" spacing={1}>
                                {detail.color && (
                                  <Typography variant="caption" color="text.secondary">
                                    Color: {detail.color}
                                  </Typography>
                                )}
                                {detail.mill && (
                                  <Typography variant="caption" color="text.secondary">
                                    Mill: {detail.mill}
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>

                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" display="block">Bags</Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {formatInteger(detail.number_of_bags)}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" display="block">Wt/Bag</Typography>
                                <Typography variant="body2">{detail.weight} kg</Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" display="block">Total</Typography>
                                <Typography variant="body2" fontWeight="bold" color="primary.main">
                                  {(detail.number_of_bags * detail.weight).toFixed(2)} kg
                                </Typography>
                              </Grid>
                            </Grid>
                          </Stack>
                        </Paper>
                      ))}

                      <Divider />

                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" fontWeight="bold">{t('total')}:</Typography>
                            <Stack direction="row" spacing={3}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">{t('receivedNotes.bags')}</Typography>
                                <Typography variant="body2" fontWeight="bold">{formatInteger(received_note?.total_bags)}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">{t('receivedNotes.weight')}</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {typeof received_note?.total_weight === 'number' 
                                    ? received_note.total_weight.toFixed(2) 
                                    : Number(received_note?.total_weight || 0).toFixed(2)} kg
                                </Typography>
                              </Box>
                            </Stack>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {t('receivedNotes.counts')}: {received_note?.counts_display}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Stack>
                  ) : (
                    /* Desktop Table View */
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('receivedNotes.counts')}</TableCell>
                            <TableCell>{t('receivedNotes.numberOfBags')}</TableCell>
                            <TableCell>{t('receivedNotes.weightPerBag')}</TableCell>
                            <TableCell>{t('receivedNotes.totalWeightKg')}</TableCell>
                            <TableCell>{t('receivedNotes.mill')}</TableCell>
                            <TableCell>{t('receivedNotes.color')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {received_note?.received_note_details.map((detail, index) => (
                            <TableRow key={index}>
                              <TableCell>{formatInteger(detail.counts)}s</TableCell>
                              <TableCell>{formatInteger(detail.number_of_bags)}</TableCell>
                              <TableCell>{detail.weight}</TableCell>
                              <TableCell>
                                <strong>{(detail.number_of_bags * detail.weight).toFixed(2)}</strong>
                              </TableCell>
                              <TableCell>{detail.mill || "-"}</TableCell>
                              <TableCell>{detail.color || "-"}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: "action.hover" }}>
                            <TableCell>
                              <strong>{t('total')}</strong>
                            </TableCell>
                            <TableCell>
                              <strong>{formatInteger(received_note?.total_bags)}</strong>
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell>
                              <strong>{typeof received_note?.total_weight === 'number' ? received_note.total_weight.toFixed(2) : Number(received_note?.total_weight || 0).toFixed(2)} kg</strong>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {t('receivedNotes.counts')}: {received_note?.counts_display}
                              </Typography>
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>

              {/* Associated Programs Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{t('receivedNotes.associatedPrograms')}</Typography>
                  {received_note?.programs && received_note.programs.length > 0 ? (
                    isMobile ? (
                      /* Mobile Card-Based View */
                      <Stack spacing={2}>
                        {received_note.programs.map((program) => {
                          const totalWeight = typeof program.total_weight === 'number' ? program.total_weight : parseFloat(program.total_weight || '0');
                          const doneWeight = typeof program.done_weight === 'number' ? program.done_weight : parseFloat(program.done_weight || '0');
                          const progress = typeof program.progress === 'number' ? program.progress : parseFloat(program.progress || '0');
                          
                          return (
                            <Paper 
                              key={program.id}
                              variant="outlined" 
                              sx={{ 
                                p: 2,
                                cursor: "pointer",
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                              onClick={() => navigate(`/programs/${program.id}`)}
                            >
                              <Stack spacing={1.5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    Program #{program.id}
                                  </Typography>
                                  <Chip
                                    label={program.status.replace(/_/g, ' ').toUpperCase()}
                                    size="small"
                                    color={
                                      program.status === "completed"
                                        ? "success"
                                        : program.status === "in_progress"
                                        ? "primary"
                                        : program.status === "delivered"
                                        ? "info"
                                        : "default"
                                    }
                                    sx={{ height: 20, fontSize: "0.65rem" }}
                                  />
                                </Stack>

                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">Dia / Gauge</Typography>
                                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center", mt: 0.5 }}>
                                    {program.program_details
                                      .filter((detail, index, self) => 
                                        index === self.findIndex((d) => 
                                          d.dia === detail.dia && d.gauge === detail.gauge && d.program_type === detail.program_type
                                        )
                                      )
                                      .map((detail, idx) => (
                                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                          <DiaCircle
                                            dia={detail.dia}
                                            gauge={detail.gauge}
                                            machineType={detail.program_type}
                                            machines={machines}
                                            size="small"
                                          />
                                          <GaugeChip gauge={detail.gauge} />
                                        </Box>
                                      ))}
                                  </Box>
                                </Box>

                                <ProgressBar
                                  type="weight"
                                  done={doneWeight}
                                  total={totalWeight}
                                  size="small"
                                />
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    ) : (
                      /* Desktop Table View */
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Program ID</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Dia / Gauge</TableCell>
                              <TableCell width="250">Weight Progress</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {received_note.programs.map((program) => {
                              const totalWeight = typeof program.total_weight === 'number' ? program.total_weight : parseFloat(program.total_weight || '0');
                              const doneWeight = typeof program.done_weight === 'number' ? program.done_weight : parseFloat(program.done_weight || '0');
                              const progress = typeof program.progress === 'number' ? program.progress : parseFloat(program.progress || '0');
                              
                              return (
                                <TableRow
                                  key={program.id}
                                  onClick={() => navigate(`/programs/${program.id}`)}
                                  sx={{
                                    cursor: "pointer",
                                    "&:hover": { bgcolor: "action.hover" },
                                  }}
                                >
                                  <TableCell>
                                    <strong>#{program.id}</strong>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={program.status.replace(/_/g, ' ').toUpperCase()}
                                      size="small"
                                      color={
                                        program.status === "completed"
                                          ? "success"
                                          : program.status === "in_progress"
                                          ? "primary"
                                          : program.status === "delivered"
                                          ? "info"
                                          : "default"
                                      }
                                      sx={{ height: 20, fontSize: "0.65rem" }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                                      {program.program_details
                                        .filter((detail, index, self) => 
                                          index === self.findIndex((d) => 
                                            d.dia === detail.dia && d.gauge === detail.gauge && d.program_type === detail.program_type
                                          )
                                        )
                                        .map((detail, idx) => (
                                          <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <DiaCircle
                                              dia={detail.dia}
                                              gauge={detail.gauge}
                                              machineType={detail.program_type}
                                              machines={machines}
                                              size="small"
                                            />
                                            <GaugeChip gauge={detail.gauge} />
                                          </Box>
                                        ))}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <ProgressBar
                                      type="weight"
                                      done={doneWeight}
                                      total={totalWeight}
                                      size="small"
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )
                  ) : (
                    <Typography variant="body2" color="text.secondary">{t('receivedNotes.noPrograms')}</Typography>
                  )}
                </CardContent>
              </Card>

              {/* Associated Deliveries Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{t('receivedNotes.associatedDeliveries')}</Typography>
                  {received_note?.deliveries && received_note.deliveries.length > 0 ? (
                    isMobile ? (
                      /* Mobile Card-Based View */
                      <Stack spacing={2}>
                        {received_note.deliveries.map((delivery) => {
                          const totalWeight = typeof delivery.calculated_total_weight === 'number' 
                            ? delivery.calculated_total_weight 
                            : parseFloat(delivery.calculated_total_weight || '0');
                          
                          return (
                            <Paper 
                              key={delivery.id}
                              variant="outlined" 
                              sx={{ 
                                p: 2,
                                cursor: "pointer",
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                              onClick={() => navigate(`/deliveries/${delivery.id}`)}
                            >
                              <Stack spacing={1.5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    Delivery #{delivery.id}
                                  </Typography>
                                  <Chip
                                    label={truncateText(`Program #${delivery.program.id}`, 20)}
                                    size="small"
                                    color="primary"
                                    sx={{ height: 20, fontSize: "0.65rem", maxWidth: PROGRAM_NAME_MAX_WIDTH.chipMobile }}
                                    title={`Program #${delivery.program.id}`}
                                  />
                                </Stack>

                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">Date</Typography>
                                  <Typography variant="body2">
                                    {new Date(delivery.delivery_date).toLocaleDateString()}
                                  </Typography>
                                </Box>

                                <Grid container spacing={2}>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" display="block">Person</Typography>
                                    <Typography variant="body2">{delivery.delivery_person_name}</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" display="block">Phone</Typography>
                                    <Typography variant="body2">{delivery.delivery_person_phone}</Typography>
                                  </Grid>
                                </Grid>

                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">Vehicle</Typography>
                                  <Typography variant="body2">{delivery.delivery_vehicle_number}</Typography>
                                </Box>

                                <Grid container spacing={2}>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" display="block">Rolls</Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {formatInteger(delivery.calculated_total_rolls)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" display="block">Weight</Typography>
                                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                                      {totalWeight.toFixed(2)} kg
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    ) : (
                      /* Desktop Table View */
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Delivery ID</TableCell>
                              <TableCell>Program</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell>Person</TableCell>
                              <TableCell>Phone</TableCell>
                              <TableCell>Vehicle</TableCell>
                              <TableCell align="right">Rolls</TableCell>
                              <TableCell align="right">Weight (kg)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {received_note.deliveries.map((delivery) => {
                              const totalWeight = typeof delivery.calculated_total_weight === 'number' 
                                ? delivery.calculated_total_weight 
                                : parseFloat(delivery.calculated_total_weight || '0');
                              
                              return (
                                <TableRow
                                  key={delivery.id}
                                  onClick={() => navigate(`/deliveries/${delivery.id}`)}
                                  sx={{
                                    cursor: "pointer",
                                    "&:hover": { bgcolor: "action.hover" },
                                  }}
                                >
                                  <TableCell>
                                    <strong>#{delivery.id}</strong>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={`#${delivery.program.id}`}
                                      size="small"
                                      color="primary"
                                      sx={{ height: 20, fontSize: "0.65rem" }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {new Date(delivery.delivery_date).toLocaleDateString()}
                                  </TableCell>
                                  <TableCell>{delivery.delivery_person_name}</TableCell>
                                  <TableCell>{delivery.delivery_person_phone}</TableCell>
                                  <TableCell>{delivery.delivery_vehicle_number}</TableCell>
                                  <TableCell align="right">
                                    {formatInteger(delivery.calculated_total_rolls)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <strong>{totalWeight.toFixed(2)}</strong>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )
                  ) : (
                    <Typography variant="body2" color="text.secondary">{t('receivedNotes.noDeliveries')}</Typography>
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              {(canUpdate('received_notes') || canDelete('received_notes')) && (
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={2} 
                  className="no-print"
                >
                  {canUpdate('received_notes') && (
                    <Button
                      variant="contained"
                      startIcon={!isMobile && <EditIcon />}
                      onClick={() => setIsEditing(true)}
                      fullWidth={isMobile}
                      disabled={isDeleting}
                      size={isMobile ? "medium" : "large"}
                    >
                      {isMobile ? <EditIcon sx={{ mr: 1 }} /> : null}
                      {t('edit')}
                    </Button>
                  )}
                  {canDelete('received_notes') && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={isDeleting ? <CircularProgress size={20} /> : (!isMobile && <DeleteIcon />)}
                      onClick={handleDelete}
                      fullWidth={isMobile}
                      disabled={isDeleting}
                      size={isMobile ? "medium" : "large"}
                    >
                      {isMobile && !isDeleting ? <DeleteIcon sx={{ mr: 1 }} /> : null}
                      {isDeleting ? t('deleting') : t('delete')}
                    </Button>
                  )}
                </Stack>
              )}
            </Box>
          )}
      </Box>

      {/* Printable Received Note - Only visible when printing */}
      {!isEditing && received_note && (
        <Paper
          elevation={0}
          className="printable-received-note"
          sx={{
            display: "none",
            "@media print": {
              display: "block !important",
            },
          }}
        >
          {/* Letterhead Container - Black & White Only */}
          <Box
            sx={{
              border: "2px solid #000",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Company Letterhead Header */}
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                borderBottom: "2px solid #000",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mb: 0.5 }}>
                <img 
                  src="/favicon-32x32.png" 
                  alt="Logo" 
                  style={{ width: "40px", height: "40px" }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "bold",
                    letterSpacing: "3px",
                    textTransform: "uppercase",
                  }}
                >
                  {orgSettings?.org_name || "DEVI IMPEX"}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                {orgSettings?.address || "Address not configured"}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 4, flexWrap: "wrap" }}>
                <Typography variant="body2">
                  Cell: {orgSettings?.phone_number || "N/A"}
                </Typography>
                <Typography variant="body2">
                  Email: {orgSettings?.email || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  GSTIN: {orgSettings?.gstin || "N/A"}
                </Typography>
              </Box>
            </Box>

            {/* Document Title */}
            <Box
              sx={{
                textAlign: "center",
                py: 1,
                borderBottom: "1px solid #000",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  letterSpacing: "2px",
                  textDecoration: "underline",
                }}
              >
                RECEIVED NOTE
              </Typography>
            </Box>

            {/* Document Info & Customer */}
            <Box sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}>
              {/* Document Number & Date Row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2,
                  pb: 1,
                  borderBottom: "1px dotted #000",
                }}
              >
                <Typography variant="h6">
                  <strong>R.N. No:</strong> {received_note.id}
                </Typography>
                <Typography variant="h6">
                  <strong>Date:</strong> {new Date(received_note.created_at).toLocaleDateString('en-GB')}
                </Typography>
              </Box>

              {/* Customer Name */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  From:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  M/s. {received_note.company.name}
                </Typography>
              </Box>

              {/* Items Table */}
              <TableContainer sx={{ mb: 2, flex: 1 }}>
                <Table size="medium" sx={{ border: "1px solid #000" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          border: "1px solid #000", 
                          fontWeight: "bold",
                          textAlign: "center",
                          py: 1.5,
                          fontSize: "1rem",
                          width: "25%",
                        }}
                      >
                        Mill - Counts
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          border: "1px solid #000", 
                          fontWeight: "bold",
                          textAlign: "center",
                          py: 1.5,
                          fontSize: "1rem",
                          width: "15%",
                        }}
                      >
                        Color
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          border: "1px solid #000", 
                          fontWeight: "bold",
                          textAlign: "center",
                          py: 1.5,
                          fontSize: "1rem",
                          width: "15%",
                        }}
                      >
                        No. of Bags
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          border: "1px solid #000", 
                          fontWeight: "bold",
                          textAlign: "center",
                          py: 1.5,
                          fontSize: "1rem",
                          width: "20%",
                        }}
                      >
                        Weight/Bag (kg)
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          border: "1px solid #000", 
                          fontWeight: "bold",
                          textAlign: "center",
                          py: 1.5,
                          fontSize: "1rem",
                          width: "25%",
                        }}
                      >
                        Total Weight (kg)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {received_note.received_note_details.map((detail, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ border: "1px solid #000", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                          {detail.mill ? `${detail.mill} - ${detail.counts}s` : `${detail.counts}s`}
                        </TableCell>
                        <TableCell sx={{ border: "1px solid #000", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                          {detail.color || "-"}
                        </TableCell>
                        <TableCell sx={{ border: "1px solid #000", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                          {detail.number_of_bags}
                        </TableCell>
                        <TableCell sx={{ border: "1px solid #000", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                          {Number(detail.weight).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ border: "1px solid #000", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                          {(detail.number_of_bags * detail.weight).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Empty rows to fill space - show at least 8 rows total */}
                    {received_note.received_note_details.length < 8 && Array.from({ length: 8 - received_note.received_note_details.length }).map((_, index) => (
                      <TableRow key={`empty-${index}`}>
                        <TableCell sx={{ border: "1px solid #000", py: 1.5 }}>&nbsp;</TableCell>
                        <TableCell sx={{ border: "1px solid #000", py: 1.5 }}>&nbsp;</TableCell>
                        <TableCell sx={{ border: "1px solid #000", py: 1.5 }}>&nbsp;</TableCell>
                        <TableCell sx={{ border: "1px solid #000", py: 1.5 }}>&nbsp;</TableCell>
                        <TableCell sx={{ border: "1px solid #000", py: 1.5 }}>&nbsp;</TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow>
                      <TableCell 
                        colSpan={2} 
                        sx={{ border: "1px solid #000", fontWeight: "bold", py: 1.5, fontSize: "1rem", textAlign: "right", pr: 2 }}
                      >
                        TOTAL:
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #000", fontWeight: "bold", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                        {received_note.total_bags}
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #000", py: 1.5 }}>&nbsp;</TableCell>
                      <TableCell sx={{ border: "1px solid #000", fontWeight: "bold", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                        {typeof received_note.total_weight === 'number' 
                          ? received_note.total_weight.toFixed(2) 
                          : Number(received_note.total_weight || 0).toFixed(2)} kg
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Representative Details */}
              {(received_note.representative_name || received_note.representative_phone) && (
                <Box 
                  sx={{ 
                    py: 1, 
                    borderTop: "1px dotted #000",
                    borderBottom: "1px dotted #000",
                    mb: 2,
                  }}
                >
                  <Typography variant="body1">
                    {received_note.representative_name && (
                      <>
                        <strong>Representative:</strong> {received_note.representative_name}
                      </>
                    )}
                    {received_note.representative_name && received_note.representative_phone && " | "}
                    {received_note.representative_phone && (
                      <>
                        <strong>Phone:</strong> {received_note.representative_phone}
                      </>
                    )}
                  </Typography>
                </Box>
              )}

              {/* Footer with Signatures */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: "auto",
                  pt: 2,
                }}
              >
                <Box sx={{ textAlign: "center", width: "40%" }}>
                  <Box sx={{ borderBottom: "1px solid #000", mb: 1, height: "50px" }} />
                  <Typography variant="body1">
                    Sender's Signature
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center", width: "40%" }}>
                  <Box sx={{ borderBottom: "1px solid #000", mb: 1, height: "50px" }} />
                  <Typography variant="body1" fontWeight="bold">
                    For {orgSettings?.org_name || "DEVI IMPEX"}
                  </Typography>
                  <Typography variant="body2">
                    Authorised Signatory
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Print Styles */}
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            
            html, body {
              height: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            body * {
              visibility: hidden;
            }
            
            .printable-received-note, .printable-received-note * {
              visibility: visible !important;
            }
            
            .no-print, .MuiCard-root, .MuiAppBar-root, .MuiDrawer-root {
              display: none !important;
            }
            
            .MuiContainer-root {
              max-width: 100% !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            
            .printable-received-note {
              position: fixed !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              height: 100vh !important;
              box-shadow: none !important;
              padding: 0 !important;
              overflow: hidden !important;
              page-break-after: avoid !important;
              page-break-inside: avoid !important;
            }
            
            .printable-received-note > div {
              height: 100% !important;
              max-height: 100vh !important;
              overflow: hidden !important;
            }
          }
        `}
      </style>
    </Container>
  );
}
